import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Home,
  Building2,
  Fence,
  ChefHat,
  Phone,
  MessageSquare,
  Mail,
  MapPin,
  Star,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/33i9k8h9_IMG_0556.JPG";
const HERO_BG = "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/2dnc7vx7_IMG_6701%20%288%29.jpg";
const TALLY_URL = "https://tally.so/r/pboq2b";
const PHONE_TEL = "+15025372762";
const PHONE_DISPLAY = "(502) 537-2762";
const CALL_TEL = "+15022510485";
const CALL_DISPLAY = "(502) 251-0485";
const EMAIL = "hernandezcompany9@gmail.com";
const WHATSAPP_URL = "https://wa.me/15025372762";
const GOOGLE_REVIEW_URL = "https://g.page/r/CfYUDOAf_uMZEAE/review";

const PORTFOLIO_IMAGES = [
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/ndd762f2_IMG_1494%20%282%29.jpg", category: "interior", alt: "Green Room Painting" },
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/geauextt_IMG_1780.jpg", category: "interior", alt: "Green Bathroom" },
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/vyg3817i_IMG_8760.jpg", category: "exterior", alt: "Green House Exterior" },
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/gxs43rkg_IMG_1779.jpg", category: "interior", alt: "Green Room with Window" },
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/afw3io3f_IMG_1787.jpg", category: "cabinets", alt: "Dark Kitchen Cabinets" },
  { url: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/7x9zdxs5_IMG_1868.jpg", category: "cabinets", alt: "Kitchen Cabinet Before After" },
  { url: "https://customer-assets.emergentagent.com/job_7ec39c3c-0582-4f68-ae1c-c8b42bdbe048/artifacts/8tkz5p2h_cabinets2026.jpg", category: "cabinets", alt: "White Kitchen Cabinets 2026" },
  { url: "https://customer-assets.emergentagent.com/job_7ec39c3c-0582-4f68-ae1c-c8b42bdbe048/artifacts/cpn7gmjf_exterior%20painting%202026.jpg", category: "exterior", alt: "Blue House Exterior 2026" },
  { url: "https://customer-assets.emergentagent.com/job_7ec39c3c-0582-4f68-ae1c-c8b42bdbe048/artifacts/calrxbr7_cabinets%20paint.jpeg", category: "cabinets", alt: "Black Cabinets Painting" },
];

const BEFORE_AFTER = [
  { url: "/portfolio/before-room.jpg", label: "Before", alt: "Room before painting - plain white walls" },
  { url: "https://customer-assets.emergentagent.com/job_7ec39c3c-0582-4f68-ae1c-c8b42bdbe048/artifacts/ry56objr_picture%202026%20today.JPG", label: "After", alt: "Room after painting - blue and white stripes" },
  { url: "https://customer-assets.emergentagent.com/job_7ec39c3c-0582-4f68-ae1c-c8b42bdbe048/artifacts/rldo8k15_picture2026%202.JPG", label: "After", alt: "Room after painting - blue and white stripes angle" },
];

const SERVICE_IMAGES = {
  interior: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/nni9nqgx_IMG_1779%20%281%29.jpg",
  exterior: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/7fi209mb_IMG_1745.jpg",
  fence: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/t2i91y8x_IMG_3910.jpg",
  cabinet: "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/2mtsmell_IMG_1571%20%281%29.jpg",
};

const TEAM_PHOTO = "https://customer-assets.emergentagent.com/job_paint-bid-connect/artifacts/yi8cm9hd_IMG_0213.jpg";

const t = {
  en: {
    nav: { services: "Services", work: "Our Work", reviews: "Reviews", contact: "Contact" },
    hero: {
      title1: "Quality Painting",
      title2: "for Louisville",
      subtitle: "Professional residential & commercial painting services within 20 miles of Louisville, Kentucky. Free estimates, quality results.",
      cta: "Get a Free Estimate",
      badge1: "Fully Insured",
      badge2: "Free Estimates",
      badge3: "Satisfaction Guaranteed",
    },
    services: {
      heading: "Our Services",
      sub: "From interior walls to exterior facades, we handle all your painting needs with precision and care.",
      items: [
        { id: "interior", title: "Interior Painting", description: "Transform your living spaces with expert interior painting", icon: Home },
        { id: "exterior", title: "Exterior Painting", description: "Protect and beautify your home's exterior", icon: Building2 },
        { id: "fence", title: "Fence Painting", description: "Restore and protect your fences", icon: Fence },
        { id: "cabinet", title: "Cabinet Painting", description: "Give your cabinets a fresh new look", icon: ChefHat },
      ],
    },
    portfolio: {
      heading: "Our Work",
      sub: "See the transformations we've created for homeowners across Louisville.",
      beforeAfterTitle: "Before & After",
      categories: { interior: "Interior", exterior: "Exterior", cabinets: "Cabinets" },
    },
    testimonials: {
      heading: "What Our Customers Say",
      sub: "We take pride in delivering exceptional results and outstanding service.",
      leaveReview: "Leave Us a Review",
      items: [
        { name: "Sarah M.", location: "Louisville, KY", text: "Painting Company Hernandez did an amazing job on our kitchen cabinets. The transformation was incredible!" },
        { name: "John D.", location: "Jeffersontown, KY", text: "Professional, punctual, and the quality of work exceeded our expectations. Highly recommend!" },
        { name: "Maria L.", location: "St. Matthews, KY", text: "They painted our entire home exterior. Great communication and beautiful results." },
      ],
    },
    cta: {
      heading: "Ready to Transform Your Space?",
      sub: "Get a free, no-obligation estimate for your painting project. We serve Louisville and surrounding areas within a 20-mile radius.",
      button: "Request Your Free Estimate",
    },
    contact: {
      heading: "Get In Touch",
      sub: "Have questions? We're here to help. Reach out to us anytime.",
      phoneLabel: "Phone / Text",
      phoneSub: "Tap to call or send us a message!",
      emailLabel: "Email",
      areaLabel: "Service Area",
      areaValue: "Louisville, KY (20-mile radius)",
      callBtn: "Call Now",
      textBtn: "Send a Text",
      whatsappBtn: "Contact us on WhatsApp",
    },
    footer: { rights: "All rights reserved." },
    sticky: { call: "Call", text: "Text", whatsapp: "WhatsApp" },
  },
  es: {
    nav: { services: "Servicios", work: "Trabajos", reviews: "Reseñas", contact: "Contacto" },
    hero: {
      title1: "Pintura de Calidad",
      title2: "para Louisville",
      subtitle: "Servicios profesionales de pintura residencial y comercial dentro de 20 millas de Louisville, Kentucky. Estimados gratis, resultados de calidad.",
      cta: "Obtén un Estimado Gratis",
      badge1: "Totalmente Asegurados",
      badge2: "Estimados Gratis",
      badge3: "Satisfacción Garantizada",
    },
    services: {
      heading: "Nuestros Servicios",
      sub: "Desde paredes interiores hasta fachadas exteriores, atendemos todas tus necesidades de pintura con precisión y cuidado.",
      items: [
        { id: "interior", title: "Pintura Interior", description: "Transforma tus espacios con pintura interior experta", icon: Home },
        { id: "exterior", title: "Pintura Exterior", description: "Protege y embellece el exterior de tu hogar", icon: Building2 },
        { id: "fence", title: "Pintura de Cercas", description: "Restaura y protege tus cercas", icon: Fence },
        { id: "cabinet", title: "Pintura de Gabinetes", description: "Dale a tus gabinetes un aspecto nuevo", icon: ChefHat },
      ],
    },
    portfolio: {
      heading: "Nuestro Trabajo",
      sub: "Mira las transformaciones que hemos creado para familias en todo Louisville.",
      categories: { interior: "Interior", exterior: "Exterior", cabinets: "Gabinetes" },
    },
    testimonials: {
      heading: "Lo Que Dicen Nuestros Clientes",
      sub: "Nos enorgullece entregar resultados excepcionales y un servicio sobresaliente.",
      leaveReview: "Déjanos una Reseña",
      items: [
        { name: "Sarah M.", location: "Louisville, KY", text: "Painting Company Hernandez hizo un trabajo increíble en los gabinetes de nuestra cocina. ¡La transformación fue espectacular!" },
        { name: "John D.", location: "Jeffersontown, KY", text: "Profesionales, puntuales y la calidad del trabajo superó nuestras expectativas. ¡Muy recomendados!" },
        { name: "Maria L.", location: "St. Matthews, KY", text: "Pintaron todo el exterior de nuestra casa. Excelente comunicación y resultados hermosos." },
      ],
    },
    cta: {
      heading: "¿Listo para Transformar tu Espacio?",
      sub: "Obtén un estimado gratis y sin compromiso para tu proyecto de pintura. Servimos Louisville y áreas cercanas dentro de un radio de 20 millas.",
      button: "Solicita tu Estimado Gratis",
    },
    contact: {
      heading: "Contáctanos",
      sub: "¿Tienes preguntas? Estamos aquí para ayudarte. Escríbenos cuando quieras.",
      phoneLabel: "Teléfono / Mensaje",
      phoneSub: "¡Toca para llamar o enviarnos un mensaje!",
      emailLabel: "Correo",
      areaLabel: "Área de Servicio",
      areaValue: "Louisville, KY (radio de 20 millas)",
      callBtn: "Llamar Ahora",
      textBtn: "Enviar Mensaje",
      whatsappBtn: "Escríbenos por WhatsApp",
    },
    footer: { rights: "Todos los derechos reservados." },
    sticky: { call: "Llamar", text: "Mensaje", whatsapp: "WhatsApp" },
  },
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const c = t.en;

  const openTally = () => window.open(TALLY_URL, "_blank");

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50">
        {/* Call-only Top Bar */}
        <a
          href={`tel:${CALL_TEL}`}
          data-testid="top-call-bar"
          className="flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-gray-900 text-center py-2.5 px-4 font-extrabold text-base sm:text-xl transition-colors"
        >
          <Phone size={22} className="shrink-0" />
          <span>Free Estimates — Call Today: {CALL_DISPLAY}</span>
        </a>
        {/* Navbar */}
        <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Painting Company Hernandez" className="h-12 w-12 rounded-full object-cover" />
              <span className="font-bold text-lg text-gray-900 hidden sm:block">Painting Company Hernandez</span>
            </a>

            <div className="hidden md:flex items-center gap-6">
              <a href="#services" className="text-gray-600 hover:text-blue-600 transition-colors">{c.nav.services}</a>
              <a href="#portfolio" className="text-gray-600 hover:text-blue-600 transition-colors">{c.nav.work}</a>
              <a href="#testimonials" className="text-gray-600 hover:text-blue-600 transition-colors">{c.nav.reviews}</a>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">{c.nav.contact}</a>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="#services" className="text-gray-600 py-2" onClick={() => setMobileMenuOpen(false)}>{c.nav.services}</a>
              <a href="#portfolio" className="text-gray-600 py-2" onClick={() => setMobileMenuOpen(false)}>{c.nav.work}</a>
              <a href="#testimonials" className="text-gray-600 py-2" onClick={() => setMobileMenuOpen(false)}>{c.nav.reviews}</a>
              <a href="#contact" className="text-gray-600 py-2" onClick={() => setMobileMenuOpen(false)}>{c.nav.contact}</a>
            </div>
          </div>
        )}
      </nav>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-28">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <img src={LOGO_URL} alt="Painting Company Hernandez" className="w-32 h-32 mx-auto rounded-full object-cover mb-8 shadow-2xl" />
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
            {c.hero.title1}<br />{c.hero.title2}
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10">{c.hero.subtitle}</p>
          <Button data-testid="hero-estimate-btn" onClick={openTally} className="bg-amber-500 hover:bg-amber-600 text-gray-900 h-14 px-10 text-lg rounded-full shadow-xl hover:scale-105 transition-transform font-semibold">
            {c.hero.cta} <ArrowRight className="ml-2" size={20} />
          </Button>

          <div className="mt-16 flex flex-wrap justify-center gap-8 text-white/80">
            <div className="flex items-center gap-2"><CheckCircle size={20} className="text-amber-500" /><span>{c.hero.badge1}</span></div>
            <div className="flex items-center gap-2"><CheckCircle size={20} className="text-amber-500" /><span>{c.hero.badge2}</span></div>
            <div className="flex items-center gap-2"><CheckCircle size={20} className="text-amber-500" /><span>{c.hero.badge3}</span></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">{c.services.heading}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{c.services.sub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.services.items.map((service) => (
              <div key={service.id} data-testid={`service-card-${service.id}`} onClick={openTally} className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all">
                <div className="h-40 overflow-hidden">
                  <img src={SERVICE_IMAGES[service.id]} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">{c.portfolio.heading}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{c.portfolio.sub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PORTFOLIO_IMAGES.map((image, index) => (
              <div key={index} className="relative aspect-[4/3] rounded-2xl overflow-hidden group">
                <img src={image.url} alt={image.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-amber-500 text-gray-900 px-3 py-1 rounded-full text-sm font-medium">{c.portfolio.categories[image.category]}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Before & After */}
          <div className="mt-20">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-10">{c.portfolio.beforeAfterTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {BEFORE_AFTER.map((item, index) => (
                <div key={index} data-testid={`before-after-${index}`} className="flex flex-col">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-md">
                    <img src={item.url} alt={item.alt} className="w-full h-full object-cover" />
                  </div>
                  <span className={`mt-3 self-center px-5 py-1.5 rounded-full text-sm font-semibold ${item.label === "Before" ? "bg-gray-200 text-gray-700" : "bg-amber-500 text-gray-900"}`}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section id="testimonials" className="py-24 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">{c.testimonials.heading}</h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">{c.testimonials.sub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {c.testimonials.items.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              data-testid="leave-review-btn"
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-full shadow-lg font-semibold transition-all hover:scale-105"
            >
              <Star size={24} className="fill-amber-500 text-amber-500" />
              {c.testimonials.leaveReview}
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-6">{c.cta.heading}</h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">{c.cta.sub}</p>
          <Button data-testid="cta-estimate-btn" onClick={openTally} className="bg-amber-500 hover:bg-amber-600 text-gray-900 h-14 px-10 text-lg rounded-full shadow-xl hover:scale-105 transition-transform font-semibold">
            {c.cta.button} <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-6">{c.contact.heading}</h2>
              <p className="text-lg text-gray-600 mb-10">{c.contact.sub}</p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <Phone size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{c.contact.phoneLabel}</p>
                    <a data-testid="contact-phone-link" href={`tel:${PHONE_TEL}`} className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors">{PHONE_DISPLAY}</a>
                    <p className="text-sm text-gray-500 mb-3">{c.contact.phoneSub}</p>
                    <div className="flex flex-wrap gap-3">
                      <a data-testid="contact-call-btn" href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105">
                        <Phone size={16} /> {c.contact.callBtn}
                      </a>
                      <a data-testid="contact-text-btn" href={`sms:${PHONE_TEL}`} className="inline-flex items-center gap-2 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105">
                        <MessageSquare size={16} /> {c.contact.textBtn}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <Mail size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{c.contact.emailLabel}</p>
                    <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors break-all">{EMAIL}</a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <MapPin size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{c.contact.areaLabel}</p>
                    <p className="text-lg font-medium text-gray-900">{c.contact.areaValue}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 flex items-center justify-center">
              <a
                data-testid="contact-whatsapp-btn"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full shadow-lg flex items-center gap-3 transition-all hover:scale-105 text-lg font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {c.contact.whatsappBtn}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Team Photo Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <img src={TEAM_PHOTO} alt="Interior Painting Work" className="w-full rounded-2xl shadow-lg" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 pb-28 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Painting Company Hernandez" className="h-10 w-10 rounded-full object-cover" />
              <span className="font-bold">Painting Company Hernandez</span>
            </div>
            <p className="text-white/60 text-sm">© {new Date().getFullYear()} Painting Company Hernandez. {c.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile Contact Bar */}
      <div data-testid="sticky-contact-bar" className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] grid grid-cols-3">
        <a data-testid="sticky-call-btn" href={`tel:${PHONE_TEL}`} className="flex flex-col items-center justify-center gap-1 py-3 text-blue-600 active:bg-blue-50">
          <Phone size={20} />
          <span className="text-xs font-semibold">{c.sticky.call}</span>
        </a>
        <a data-testid="sticky-text-btn" href={`sms:${PHONE_TEL}`} className="flex flex-col items-center justify-center gap-1 py-3 text-gray-700 border-x border-gray-200 active:bg-gray-50">
          <MessageSquare size={20} />
          <span className="text-xs font-semibold">{c.sticky.text}</span>
        </a>
        <a data-testid="sticky-whatsapp-btn" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-1 py-3 text-green-600 active:bg-green-50">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="text-xs font-semibold">{c.sticky.whatsapp}</span>
        </a>
      </div>
    </div>
  );
}
